import { z } from 'zod'

import { nanoidSchema } from '@scalar-org/entities/utility'

import { type Head, headSchema } from '@/schema/head'
import { type Icon, iconSchema } from '@/schema/utility'

/** Example page navigation entry. */
const _examplePage: PageNavigationEntry = {
  type: 'page',
  filepath: 'pages/fire.md',
  title: 'Fire',
  layout: {
    toc: false,
    header: false,
    sidebar: false,
    tabs: false,
  },
  head: {
    meta: [
      {
        name: 'twitter:title',
        content: 'Your page title',
      },
      {
        name: 'description',
        content: 'A description of the page',
        id: 'some-id',
        property: 'og',
      },
    ],
    styles: [
      {
        path: '/some-stylesheet.css',
      },
      {
        path: '/some-other-stylesheet.css',
        tagPosition: 'bodyOpen',
      },
    ],
    scripts: [
      {
        path: '/some-script.js',
      },
      {
        path: '/some-other-script.js',
        tagPosition: 'bodyClose',
      },
    ],
    links: [
      {
        rel: 'icon',
        type: 'image/x-icon',
        href: 'https://example.com/favicon.ico',
      },
    ],
  },
}

// ---------------------------------------------------------------------------

export type PageFrontmatterLayout = {
  toc?: boolean
  header?: boolean
  sidebar?: boolean
  tabs?: boolean
  pageTitle?: boolean
  pageActions?: boolean
}

export type PageFrontmatter = {
  title?: string
  description?: string
  icon?: Icon
  layout?: PageFrontmatterLayout
  tag?: string
  head?: Head
}

export const pageFrontmatterSchema = z.object({
  /** Primary title of the page. Will be added as an H1 if no other title is provided. Will be used in page links elements to reference the page. */
  title: z
    .string()
    .optional()
    .describe(
      'Primary title of the page. Will be added as an H1 if no other title is provided. Will be used in page links elements to reference the page.',
    ),
  /** Optional description of the page. Will be shown in the page header. */
  description: z
    .string()
    .optional()
    .describe(
      'Optional description of the page. Will be shown in the page header.',
    ),
  /** Optional icon string (or URL) to show beside the sidebar entry for the page. */
  icon: iconSchema.optional(),
  /** Optional tag to show in the sidebar for the page. */
  tag: z
    .string()
    .optional()
    .describe('Optional tag to show in the sidebar for the page.'),
  /** Optional layout options for the page. This will override the global layout options. */
  layout: z
    .object({
      toc: z.boolean().optional(),
      header: z.boolean().optional(),
      sidebar: z.boolean().optional(),
      tabs: z.boolean().optional(),
      pageTitle: z.boolean().optional(),
      pageActions: z.boolean().optional(),
    })
    .optional()
    .describe(
      'Optional layout options for the page. This will override the global layout options.',
    ),
  /** Optional head options (meta, scripts, etc-). */
  head: headSchema.optional(),
}) satisfies z.ZodType<PageFrontmatter>

export type BasePageNavigationEntry = PageFrontmatter & {
  type: 'page'
  filepath: string
  showInSidebar?: boolean
}

const basePageNavigationEntrySchema = pageFrontmatterSchema.extend({
  type: z.literal('page'),
  /** Relative path to the page file with respect to the configuration root. */
  filepath: z
    .string()
    .describe(
      'Relative path to the page file with respect to the configuration root.',
    ),
  showInSidebar: z.boolean().default(true),
}) satisfies z.ZodType<BasePageNavigationEntry>

export type PageNavigationEntry = BasePageNavigationEntry | string

/**
 * We allow pages to be defined as a simple path (most properties will be set in the frontmatter)
 * or as an object. Frontmatter
 */
export const pageNavigationEntrySchema = z.union([
  basePageNavigationEntrySchema,
  z.string(),
]) satisfies z.ZodType<PageNavigationEntry>

// Internal ---------------------------------------------------------------------------

export type Breadcrumb = {
  type: 'root-group' | 'group' | 'page'
  path: string
  title: string
  uid?: string
}

export const breadcrumbSchema = z.object({
  type: z.enum(['root-group', 'group', 'page']),
  path: z.string(),
  title: z.string(),
  uid: nanoidSchema.optional(),
}) satisfies z.ZodType<Breadcrumb>

const baseInternalPageSchema = z.object({
  uid: nanoidSchema,
  isMdx: z.boolean(),
  isHtml: z.boolean(),
  isIndex: z.boolean(),
  version: z.string(),
  breadcrumbs: breadcrumbSchema.array(),
  title: z.string(),
  description: z.string(),
})
type BaseInternalPageSchema = z.infer<typeof baseInternalPageSchema>

export type InternalPageNavigationEntry = BasePageNavigationEntry &
  BaseInternalPageSchema
export const internalPageNavigationEntrySchema =
  basePageNavigationEntrySchema.extend(
    baseInternalPageSchema.shape,
  ) satisfies z.ZodType<InternalPageNavigationEntry>
